import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Client } from '../../../types';
import { apiClient } from '../../../services/apiClient';
import './ClientSelector.css';

const CUSTOM_OPTION_VALUE = '__custom__';
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export interface ClientSelectorProps {
  clients: Client[];
  selectedClientId: number | null;
  onChange: (id: number | null) => void;
  disabled: boolean;
  allowCustomSearch: boolean;
  onCustomPick: (client: Client) => void;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  clients,
  selectedClientId,
  onChange,
  disabled,
  allowCustomSearch,
  onCustomPick,
}) => {
  const [mode, setMode] = useState<'normal' | 'custom'>('normal');
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<Client[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // External selection changes (e.g. resetForm or auto-match) collapse the search panel.
  useEffect(() => {
    if (selectedClientId !== null) {
      setMode('normal');
      setQuery('');
      setResults([]);
      setSearchError(null);
    }
  }, [selectedClientId]);

  // Debounced search. Cancels the previous in-flight request on every change.
  useEffect(() => {
    if (mode !== 'custom') return;

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      abortRef.current?.abort();
      abortRef.current = null;
      setResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsSearching(true);
      setSearchError(null);
      try {
        const result = await apiClient.searchKlanten(trimmed, 20, controller.signal);
        if (controller.signal.aborted) return;
        setResults(Array.isArray(result?.klanten) ? result.klanten : []);
      } catch (error: any) {
        if (controller.signal.aborted || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        setResults([]);
        setSearchError(error?.message || 'Fout bij zoeken');
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, mode]);

  // Cancel any in-flight request on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === CUSTOM_OPTION_VALUE) {
      setMode('custom');
      return;
    }
    setMode('normal');
    onChange(Number(value) || null);
  };

  const handlePick = (client: Client) => {
    abortRef.current?.abort();
    abortRef.current = null;
    onCustomPick(client);
  };

  const handleQueryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setResults([]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0) {
        handlePick(results[0]);
      }
    }
  };

  const trimmedQuery = query.trim();
  const showResultsArea = mode === 'custom' && trimmedQuery.length >= MIN_QUERY_LENGTH;
  const selectValue = mode === 'custom' ? CUSTOM_OPTION_VALUE : (selectedClientId || '');

  return (
    <div className="form-section">
      <label className="form-label">
        Selecteer klant <span className="required">*</span>
      </label>
      <div className="select-wrapper">
        <select
          value={selectValue}
          onChange={handleSelectChange}
          disabled={disabled}
        >
          <option value="">Kies een klant</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.klantnaam} - {client.klantnummer}
            </option>
          ))}
          {allowCustomSearch && (
            <option value={CUSTOM_OPTION_VALUE}>Aangepast — zoek klant…</option>
          )}
        </select>
        <span className="material-icons-round select-arrow">expand_more</span>
      </div>

      {mode === 'custom' && (
        <div className="custom-search">
          <div className="custom-search-input-wrapper">
            <span className="material-icons-round custom-search-icon">search</span>
            <input
              type="text"
              className="custom-search-input"
              placeholder="Zoek op klantnaam of klantnummer…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleQueryKeyDown}
              disabled={disabled}
              autoFocus
            />
          </div>

          {trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH && (
            <p className="custom-search-hint">Typ minstens {MIN_QUERY_LENGTH} tekens om te zoeken.</p>
          )}

          {showResultsArea && (
            <ul className="custom-search-results">
              {isSearching && (
                <li className="custom-search-status">Zoeken…</li>
              )}
              {!isSearching && searchError && (
                <li className="custom-search-status custom-search-status--error">{searchError}</li>
              )}
              {!isSearching && !searchError && results.length === 0 && (
                <li className="custom-search-status">Geen klanten gevonden</li>
              )}
              {!isSearching && !searchError && results.map((client) => (
                <li key={client.id}>
                  <button
                    type="button"
                    className="custom-search-result"
                    onClick={() => handlePick(client)}
                    disabled={disabled}
                  >
                    <span className="custom-search-result-name">{client.klantnaam}</span>
                    <span className="custom-search-result-number">{client.klantnummer}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSelector;
